import ImagePreview from '@cube3/ui/src/previews/ImagePreview/ImagePreview';
import React, { useCallback, useEffect, useMemo } from 'react';
import PreviewHoc from '../../ContentPane/PreviewHoc';
import { useGenerativeContext } from '../contexts/GenerativeContext';
import Loader from '@cube3/cubicle/src/core/atoms/Loader/Loader';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { ImageGrid } from '@cube3/cubicle/src/core/organisms/ImageGrid';
import { Card } from '@cube3/cubicle/src/core/atoms/Card';
import { cardConfig } from '../config/cardConfig';

interface Props {
  error?: string;
  setError?: (err) => void;
  loading: boolean;
  withBaseImage?: boolean;
}

const ConnectedImagePreview = PreviewHoc(ImagePreview);

const copyImage = (file: File) => {
  const clipboardItem = new ClipboardItem({ [file.type]: file });
  navigator.clipboard.write([clipboardItem]);
};

export const GenerationViewer = ({
  loading,
  setError,
  error,
  withBaseImage
}: Props) => {
  const { selections, activeGeneration, setSelections } =
    useGenerativeContext();

  const generationConfig = useMemo(() => {
    // Prevent image from refreshing after URL changed from original URL to Blob URL
    // if (!activeGeneration?.file) return {};
    const { prompt, width, height, url } = activeGeneration || {};
    return {
      display_name: prompt,
      dimensions: {
        width,
        height
      },
      src: url,
      thumbnail: url
    };
  }, [activeGeneration]);

  const images = useMemo(() => {
    return selections?.map((asset) => {
      const source = asset?.media?.find((m) => m.type === 'source');
      const poster = asset?.media?.find((m) => m.type === 'poster');
      const preview = asset?.media?.find((m) => m.type === 'preview');
      const src = preview || poster;
      return {
        ...asset,
        dimensions: {
          width: source?.width,
          height: source?.height
        },
        src: src?.url
      };
    });
  }, [selections]);

  const cachedImage = activeGeneration?.file;
  const onCopy = useCallback(() => {
    if (!cachedImage) return;
    copyImage(cachedImage);
  }, [cachedImage]);

  // clear error whenever the active image changes
  useEffect(() => {
    setError(null);
  }, [activeGeneration]);

  const empty = !activeGeneration?.url && !selections?.length;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: `calc(100% - 80px)`,
        backgroundColor: '#000'
      }}
    >
      <>
        {loading || empty || error ? (
          <ImageOverlay
            loading={loading}
            messageConfig={
              error
                ? { ...cardConfig.error, content: error }
                : cardConfig.empty[`${withBaseImage}`]
            }
          />
        ) : activeGeneration ? (
          <ConnectedImagePreview
            key={`preview-generation-${activeGeneration.id}`}
            onCopy={onCopy}
            {...generationConfig}
          />
        ) : (
          <div
            id={'selected-reference-assets'}
            style={{ width: '100%', height: '100%' }}
          >
            <ImageGrid images={images} onClickClose={setSelections} />
          </div>
        )}
      </>
    </div>
  );
};

const ImageOverlay = ({ loading, messageConfig }) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)'
      }}
    >
      {loading ? (
        <Loader size={SizeVariants['2xl']} />
      ) : (
        <Card {...messageConfig} size="lg" />
      )}
    </div>
  );
};
